import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faCheckCircle,
    faTimesCircle,
    faExclamationCircle,
    faFileInvoice,
    faDollarSign,
    faStamp,
    faRotateLeft
} from '@fortawesome/free-solid-svg-icons';
import { getErrorMessage } from '../../utils/errorMessages';
import './FinancialApprovalModal.css';

const FinancialApprovalModal = ({
    isOpen,
    onClose,
    invoice,
    isFinancialApprover,
    onApprovalAction,
    documentType = 'Invoice',
    entityStampUrl = null,
    allowUndo = false,
    // Plain sentences the approver should read before stamping, e.g. a
    // supplier line whose quantity is not the delivery's BDN (ledger 166).
    warnings = []
}) => {
    const [selectedAction, setSelectedAction] = useState(null); // 'reject' (approve/undo submit directly)
    const [rejectionReason, setRejectionReason] = useState('');
    const [notes, setNotes] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [stampImgFailed, setStampImgFailed] = useState(false);

    if (!isOpen || !invoice) return null;

    // Submit an action straight through. Approving (pressing the stamp) and
    // undoing are single-press actions; rejecting requires a reason first.
    const submitAction = async (action, { reason = '' } = {}) => {
        if (action === 'reject' && !reason.trim()) {
            setError(`Rejection reason is required when rejecting ${documentType.toLowerCase()}`);
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            await onApprovalAction({
                action,
                rejection_reason: reason,
                notes
            });

            // Reset form
            setSelectedAction(null);
            setRejectionReason('');
            setNotes('');
            onClose();
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to process approval. Please try again.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget && !isSubmitting) {
            onClose();
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatStatus = (status) => {
        if (!status) return 'N/A';
        // Convert underscore-separated status to Title Case
        return status
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    const isPending = invoice.status === 'PENDING_APPROVAL';
    const isApproved = invoice.status === 'APPROVED';

    return (
        <div className="financial-approval-modal-overlay" onClick={handleOverlayClick}>
            <div className="financial-approval-modal">
                {/* Header */}
                <div className="financial-approval-modal-header">
                    <div className="financial-approval-modal-header-content">
                        <h3>
                            <FontAwesomeIcon icon={faFileInvoice} /> Financial Approval
                        </h3>
                        <div className="financial-approval-invoice-number">
                            {documentType}: {invoice.invoice_number}
                        </div>
                    </div>
                    <button
                        className="financial-approval-close-button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        type="button"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Body */}
                <div className="financial-approval-modal-body">
                    {/* Invoice Summary */}
                    <div className="financial-approval-invoice-summary">
                        <h4>{documentType} Details</h4>
                        <div className="financial-approval-detail-grid">
                            <div className="financial-approval-detail-item">
                                <span className="financial-approval-detail-label">Partner:</span>
                                <span className="financial-approval-detail-value">{invoice.partner_name}</span>
                            </div>
                            <div className="financial-approval-detail-item">
                                <span className="financial-approval-detail-label">Invoice Date:</span>
                                <span className="financial-approval-detail-value">{formatDate(invoice.invoice_date)}</span>
                            </div>
                            <div className="financial-approval-detail-item">
                                <span className="financial-approval-detail-label">Due Date:</span>
                                <span className="financial-approval-detail-value">{formatDate(invoice.due_date)}</span>
                            </div>
                            <div className="financial-approval-detail-item">
                                <span className="financial-approval-detail-label">Entity:</span>
                                <span className="financial-approval-detail-value">{invoice.hms_entity_name || 'N/A'}</span>
                            </div>
                            <div className="financial-approval-detail-item financial-approval-amount-highlight">
                                <span className="financial-approval-detail-label">
                                    <FontAwesomeIcon icon={faDollarSign} /> Total Amount:
                                </span>
                                <span className="financial-approval-detail-value">
                                    {formatCurrency(invoice.gross_amount, invoice.currency)}
                                </span>
                            </div>
                            <div className="financial-approval-detail-item">
                                <span className="financial-approval-detail-label">Status:</span>
                                <span className={`financial-approval-status-badge ${invoice.status?.toLowerCase()}`}>
                                    {formatStatus(invoice.status)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {warnings.length > 0 && (
                        <div className="financial-approval-warnings" role="note">
                            <FontAwesomeIcon icon={faExclamationCircle} />
                            <div>
                                <strong>Check before approving</strong>
                                <ul>
                                    {warnings.map((warning) => <li key={warning}>{warning}</li>)}
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Check if user is Financial Approver */}
                    {!isFinancialApprover ? (
                        <div className="financial-approval-error-message">
                            <FontAwesomeIcon icon={faExclamationCircle} />
                            <span>You do not have permission to approve {documentType.toLowerCase()}s. Only Financial Approvers can perform this action.</span>
                        </div>
                    ) : isPending ? (
                        /* ============ PENDING: press-to-stamp approve, or reject ============ */
                        <>
                            <div className="financial-approval-actions-section">
                                <h4>Approve by stamping</h4>

                                {/* Optional approval notes */}
                                <div className="financial-approval-form-section" style={{ marginTop: 0 }}>
                                    <label>Approval Notes (Optional)</label>
                                    <textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Add any additional notes or comments about this approval..."
                                        disabled={isSubmitting}
                                    />
                                </div>

                                {/* Stamp press button */}
                                {entityStampUrl && !stampImgFailed ? (
                                    <button
                                        type="button"
                                        onClick={() => submitAction('approve')}
                                        disabled={isSubmitting}
                                        title="Press to apply your approval stamp"
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '10px',
                                            width: '100%',
                                            padding: '18px',
                                            marginTop: '12px',
                                            border: '2px dashed #4a6b25',
                                            borderRadius: '12px',
                                            background: 'transparent',
                                            cursor: isSubmitting ? 'wait' : 'pointer',
                                            transition: 'transform 0.12s ease, background 0.12s ease'
                                        }}
                                        onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; }}
                                        onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                                    >
                                        <img
                                            src={entityStampUrl}
                                            alt="Approval stamp"
                                            onError={() => setStampImgFailed(true)}
                                            style={{ width: '140px', height: '140px', objectFit: 'contain' }}
                                        />
                                        <span style={{ fontWeight: 500, color: '#4a6b25' }}>
                                            <FontAwesomeIcon icon={faStamp} />{' '}
                                            {isSubmitting ? 'Stamping…' : `Press to stamp & approve ${documentType.toLowerCase()}`}
                                        </span>
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="financial-approval-submit-button"
                                        onClick={() => submitAction('approve')}
                                        disabled={isSubmitting}
                                        style={{ marginTop: '12px' }}
                                    >
                                        <FontAwesomeIcon icon={faCheckCircle} />{' '}
                                        {isSubmitting ? 'Processing…' : `Approve ${documentType}`}
                                    </button>
                                )}
                            </div>

                            {/* Reject toggle */}
                            <div className="financial-approval-actions-section">
                                {selectedAction !== 'reject' ? (
                                    <button
                                        type="button"
                                        className="financial-approval-action-button reject"
                                        onClick={() => { setSelectedAction('reject'); setError(''); }}
                                        disabled={isSubmitting}
                                    >
                                        <FontAwesomeIcon icon={faTimesCircle} /> Reject instead
                                    </button>
                                ) : (
                                    <div className="financial-approval-form-section">
                                        <label>
                                            Rejection Reason <span className="required">*</span>
                                        </label>
                                        <textarea
                                            value={rejectionReason}
                                            onChange={(e) => setRejectionReason(e.target.value)}
                                            placeholder={`Please provide a detailed reason for rejecting this ${documentType.toLowerCase()}...`}
                                            disabled={isSubmitting}
                                        />
                                        <button
                                            type="button"
                                            className="financial-approval-submit-button"
                                            onClick={() => submitAction('reject', { reason: rejectionReason })}
                                            disabled={isSubmitting}
                                            style={{ marginTop: '10px' }}
                                        >
                                            <FontAwesomeIcon icon={faTimesCircle} />{' '}
                                            {isSubmitting ? 'Processing…' : `Reject ${documentType}`}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {error && (
                                <div className="financial-approval-error-message">
                                    <FontAwesomeIcon icon={faExclamationCircle} />
                                    <span>{error}</span>
                                </div>
                            )}
                        </>
                    ) : (allowUndo && isApproved) ? (
                        /* ============ APPROVED: show stamp + undo ============ */
                        <>
                            <div className="financial-approval-actions-section">
                                <h4>Approved</h4>
                                <div className="financial-approval-detail-grid">
                                    {invoice.approved_by_name && (
                                        <div className="financial-approval-detail-item">
                                            <span className="financial-approval-detail-label">Approved by:</span>
                                            <span className="financial-approval-detail-value">{invoice.approved_by_name}</span>
                                        </div>
                                    )}
                                    {invoice.approved_at && (
                                        <div className="financial-approval-detail-item">
                                            <span className="financial-approval-detail-label">Approved on:</span>
                                            <span className="financial-approval-detail-value">{formatDate(invoice.approved_at)}</span>
                                        </div>
                                    )}
                                </div>

                                {entityStampUrl && !stampImgFailed && (
                                    <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
                                        <img
                                            src={entityStampUrl}
                                            alt="Applied approval stamp"
                                            onError={() => setStampImgFailed(true)}
                                            style={{ width: '120px', height: '120px', objectFit: 'contain', transform: 'rotate(-8deg)' }}
                                        />
                                    </div>
                                )}

                                <p className="financial-approval-hint" style={{ fontSize: '13px', color: '#666' }}>
                                    Undoing returns this {documentType.toLowerCase()} to <strong>Pending Approval</strong> and removes the stamp from the PDF. The invoice number is kept. This is only possible before the {documentType.toLowerCase()} is sent.
                                </p>

                                <button
                                    type="button"
                                    className="financial-approval-action-button reject"
                                    onClick={() => submitAction('undo')}
                                    disabled={isSubmitting}
                                    style={{ marginTop: '8px' }}
                                >
                                    <FontAwesomeIcon icon={faRotateLeft} />{' '}
                                    {isSubmitting ? 'Undoing…' : 'Undo approval (un-stamp)'}
                                </button>
                            </div>

                            {error && (
                                <div className="financial-approval-error-message">
                                    <FontAwesomeIcon icon={faExclamationCircle} />
                                    <span>{error}</span>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="financial-approval-error-message">
                            <FontAwesomeIcon icon={faExclamationCircle} />
                            <span>This {documentType.toLowerCase()} is not pending approval. Current status: {formatStatus(invoice.status)}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FinancialApprovalModal;
